import { useCallback, useEffect, useState } from "react";
import {
  X,
  ShieldAlert,
  Loader2,
  Building2,
  Users,
  Play,
  Square,
} from "lucide-react";
import {
  getOrganizations,
  getTeamsByOrg,
  startTeamScenarioAsPlatformAdmin,
  stopTeamScenarioAsPlatformAdmin,
  getAllOrgStartedTeamScenarios,
} from "../../api/services/platformAdminService";

export default function PlatformTeamChallengeModal({
  open,
  onClose,
  scenarioId,
}) {
  // ── Organizations ──
  const [organizations, setOrganizations] = useState([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [orgsError, setOrgsError] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");

  // ── All active runs across every org (fetched once on open) ──
  // Set of team ids that have an active run for THIS scenario.
  const [runningTeamIds, setRunningTeamIds] = useState(new Set());

  // ── Teams for the selected org ──
  const [teams, setTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamsError, setTeamsError] = useState("");
  const [busyTeamId, setBusyTeamId] = useState(null);

  // Build the set of team ids running THIS scenario from the all-org runs.
  const buildRunningSet = useCallback(
    (runs) => {
      const now = Date.now();
      const ids = (runs || [])
        .filter(
          (run) =>
            run.scenario_id === scenarioId &&
            new Date(run.expires_at).getTime() > now,
        )
        .map((run) => run.team_id);
      return new Set(ids);
    },
    [scenarioId],
  );

  // Re-fetch all-org runs and rebuild the running set (used after start/stop
  // errors to resync with the server).
  const refreshRunningSet = useCallback(async () => {
    try {
      const data = await getAllOrgStartedTeamScenarios();
      setRunningTeamIds(buildRunningSet(data?.runs));
    } catch {
      /* keep current set on failure */
    }
  }, [buildRunningSet]);

  // On open: load organizations + all-org running state in parallel.
  useEffect(() => {
    if (!open) return;
    let isActive = true;

    const load = async () => {
      setIsLoadingOrgs(true);
      setOrgsError("");
      setSelectedOrgId("");
      setTeams([]);
      setRunningTeamIds(new Set());
      try {
        const [orgsData, startedData] = await Promise.all([
          getOrganizations({ page: 1, pageSize: 100 }),
          getAllOrgStartedTeamScenarios(),
        ]);
        if (!isActive) return;
        const items = Array.isArray(orgsData?.organizations)
          ? orgsData.organizations
          : [];
        setOrganizations(items);
        setRunningTeamIds(buildRunningSet(startedData?.runs));
      } catch (error) {
        if (isActive) {
          setOrgsError(error?.message || "Failed to load organizations.");
        }
      } finally {
        if (isActive) setIsLoadingOrgs(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [open, buildRunningSet]);

  // Load teams whenever a different org is selected. Running state already
  // lives in runningTeamIds (from the all-org call), so no per-team fetches.
  useEffect(() => {
    if (!selectedOrgId) {
      setTeams([]);
      return;
    }
    let isActive = true;

    const loadTeams = async () => {
      setIsLoadingTeams(true);
      setTeamsError("");
      setTeams([]);
      try {
        const data = await getTeamsByOrg({
          orgId: selectedOrgId,
          page: 1,
          pageSize: 100,
        });
        if (!isActive) return;
        const items = Array.isArray(data?.teams) ? data.teams : [];
        setTeams(items);
      } catch (error) {
        if (isActive) {
          setTeamsError(error?.message || "Failed to load teams.");
        }
      } finally {
        if (isActive) setIsLoadingTeams(false);
      }
    };

    loadTeams();
    return () => {
      isActive = false;
    };
  }, [selectedOrgId]);

  const handleToggle = async (team) => {
    if (!scenarioId) return;
    const isRunning = runningTeamIds.has(team.id);
    setBusyTeamId(team.id);
    try {
      if (isRunning) {
        await stopTeamScenarioAsPlatformAdmin({ scenarioId, teamId: team.id });
        setRunningTeamIds((prev) => {
          const next = new Set(prev);
          next.delete(team.id);
          return next;
        });
      } else {
        await startTeamScenarioAsPlatformAdmin({ scenarioId, teamId: team.id });
        setRunningTeamIds((prev) => new Set(prev).add(team.id));
      }
    } catch {
      // On error, resync the whole running set from the server so this row
      // (and any others) reflect the true state.
      await refreshRunningSet();
    } finally {
      setBusyTeamId(null);
    }
  };

  if (!open) return null;

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);
  // Count running among the CURRENTLY listed teams.
  const runningInOrg = teams.filter((t) => runningTeamIds.has(t.id)).length;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-(--color-bg)/70 rounded-sm shadow-2xl w-full max-w-2xl h-150 max-h-[calc(100vh-2rem)] border border-(--color-border) overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative flex items-start justify-between px-7 py-5 border-b border-(--color-border) shrink-0">
          <div
            className="absolute top-0 left-0 right-0 h-px pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent, var(--color-primary), transparent)",
            }}
          />
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-md bg-(--color-primary)/10 border border-(--color-primary)/20 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-5 h-5 text-(--color-primary)" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-(--color-text-primary) leading-tight">
                Team Challenge
              </h2>
              <p className="text-xs text-(--color-text-muted) mt-1">
                Select an organization, then start or stop per team
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-sm text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 px-7 py-5 flex flex-col gap-5">
          {/* Section 1 — Organizations (fixed height, internal scroll) */}
          <div className="rounded-md border border-(--color-border) p-4 flex flex-col h-40 shrink-0">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-muted)">
                Organizations
              </p>
              {!isLoadingOrgs && !orgsError && organizations.length > 0 && (
                <span className="text-[10.5px] font-medium text-(--color-text-muted)">
                  {organizations.length} total
                </span>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
              {isLoadingOrgs ? (
                <div className="flex items-center justify-center h-full gap-2 text-(--color-text-muted)">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading organizations...</span>
                </div>
              ) : orgsError ? (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">
                  {orgsError}
                </div>
              ) : organizations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                  <Building2 className="w-8 h-8 text-(--color-text-muted)/40" />
                  <p className="text-sm text-(--color-text-muted)">
                    No organizations found.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 content-start">
                  {organizations.map((org) => {
                    const checked = selectedOrgId === org.id;
                    return (
                      <button
                        key={org.id}
                        type="button"
                        onClick={() => setSelectedOrgId(org.id)}
                        className={`relative flex flex-col items-center text-center gap-1.5 px-2 py-3 rounded-md border transition-colors ${
                          checked
                            ? "border-(--color-primary) bg-(--color-primary)/8"
                            : "border-(--color-border) hover:border-(--color-border)/80"
                        }`}
                      >
                        <span
                          className={`absolute top-1.5 left-1.5 w-3.5 h-3.5 rounded-sm border-[1.5px] flex items-center justify-center transition-all ${
                            checked
                              ? "bg-(--color-primary) border-(--color-primary)"
                              : "border-(--color-border)"
                          }`}
                        >
                          {checked && (
                            <span className="w-1 h-1 rounded-full bg-white dark:bg-black" />
                          )}
                        </span>
                        <div className="w-8 h-8 rounded-md bg-(--color-bg) border border-(--color-border) flex items-center justify-center shrink-0">
                          <Building2 className="w-4 h-4 text-(--color-text-muted)" />
                        </div>
                        <span className="text-xs font-semibold text-(--color-text-primary) w-full truncate">
                          {org.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Section 2 — Teams of the selected org */}
          <div className="flex-1 min-h-0 rounded-md border border-(--color-border) p-4 flex flex-col">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-muted)">
                {selectedOrg ? `Teams — ${selectedOrg.name}` : "Teams"}
              </p>
              {selectedOrgId &&
                !isLoadingTeams &&
                !teamsError &&
                teams.length > 0 && (
                  <span className="text-[10.5px] font-medium text-(--color-text-muted)">
                    {runningInOrg} of {teams.length} running
                  </span>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
              {!selectedOrgId ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                  <ShieldAlert className="w-8 h-8 text-(--color-text-muted)/40" />
                  <p className="text-sm text-(--color-text-muted)">
                    Select an organization to continue.
                  </p>
                </div>
              ) : isLoadingTeams ? (
                <div className="flex items-center justify-center h-full gap-2 text-(--color-text-muted)">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading teams...</span>
                </div>
              ) : teamsError ? (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">
                  {teamsError}
                </div>
              ) : teams.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                  <Users className="w-8 h-8 text-(--color-text-muted)/40" />
                  <p className="text-sm text-(--color-text-muted)">
                    No teams found for this organization.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {teams.map((team) => {
                    const isRunning = runningTeamIds.has(team.id);
                    const isBusy = busyTeamId === team.id;
                    return (
                      <div
                        key={team.id}
                        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-md border transition-colors ${
                          isRunning
                            ? "border-(--color-primary)/30 bg-(--color-primary)/4"
                            : "border-(--color-border) hover:border-(--color-border)/80"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-md bg-(--color-bg) border border-(--color-border) flex items-center justify-center shrink-0">
                            <Users className="w-4 h-4 text-(--color-text-muted)" />
                          </div>
                          <div className="min-w-0">
                            <span className="block text-sm font-semibold text-(--color-text-primary) truncate">
                              {team.name}
                            </span>
                            {isRunning && (
                              <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-(--color-primary)">
                                <span className="w-1.5 h-1.5 rounded-full bg-(--color-primary) animate-pulse" />
                                Running
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle(team)}
                          disabled={isBusy}
                          className={`shrink-0 min-w-23 px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            isRunning
                              ? "bg-transparent border border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                              : "bg-(--color-primary)/10 border border-(--color-primary)/40 text-(--color-primary) hover:bg-(--color-primary)/20"
                          }`}
                        >
                          {isBusy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : isRunning ? (
                            <Square className="w-3.5 h-3.5" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          {isBusy
                            ? isRunning
                              ? "Stopping"
                              : "Starting"
                            : isRunning
                              ? "Stop"
                              : "Start"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-(--color-border) flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}